import { ViewController } from 'elc-service';
import { SearchLoadedUseCase } from '../use-cases/SearchLoadedUseCase';
import { SearchRepository } from '../repositories/SearchRepository';
import { computed, action, observable } from 'mobx';
import {
    ENDLESS_SCROLL_DEBOUNCE_LIMIT,
    ENDLESS_SCROLL_ALLOWED_PIXELS,
    STANDARD_SEARCH_PARAM,
    VOICE_SEARCH_PARAM,
    PREDICTIVE_SEARCH_PARAM,
    TAGS_SEARCH_PARAM,
    SEARCH_PARAM,
    SEARCH_TEXT_PARAM,
    FILTERS_SEARCH_PARAM,
    SEARCH_TYPE_PARAM,
    SEARCH_REDIRECT_PARAM,
    TAB_TYPE,
    PREDICTIVE_CONTENT_SEARCH_PARAM,
    STANDARD_PRODUCTS_SEARCH_PARAM,
    STANDARD_CONTENT_SEARCH_PARAM,
    TRENDING_SEARCH_PARAM
} from '../constants/Search';
import { root } from 'elc-universal-utils';
import { events, emit } from 'elc-service-bus';
import { ISortOption } from '../interfaces/ISearchInterfaces';

export class SearchResultsViewController extends ViewController {
    @observable private searchTerm: string;
    @observable private queryState: string;
    @observable private sortOptionName: string;
    @observable private searchRepository: SearchRepository;
    @observable private resultsLoaded: boolean = false;
    @observable private isSortOverlayOpen: boolean = false;
    @observable private isFiltersOverlayOpen: boolean = false;
    @observable private searchDisplayFormat: string;
    @observable private currentTab: string | null;
    @observable private currentContentTab: string = TAB_TYPE.PRODUCTS;
    @observable private isLoadingMore: boolean = false;
    @observable private searchedTerm: string = '';
    @observable private isEmptySearch: boolean = false;
    @observable private isRedirectSearch: boolean = true;
    @observable private searchType: string;
    @observable private enableContentSearch: boolean = false;
    @observable private articleSearch: boolean = false;
    @observable private refinementSearch: boolean = false;
    private endlessScrollTimeout: number;

    constructor(searchRepository: SearchRepository) {
        super();
        this.searchRepository = searchRepository;
        this.isLoading = false;
    }

    @computed
    get data() {
        return {
            productIds: this.productIds,
            skuIds: this.skuIds,
            items: this.items,
            sortOptions: this.sortOptions,
            sortOptionSelected: this.sortOptionSelected,
            isSortOverlayOpen: this.isSortOverlayOpen,
            isFiltersOverlayOpen: this.isFiltersOverlayOpen,
            breadcrumbs: this.breadcrumbs,
            navigationFilters: this.navigationFilters,
            uniqueDimensionName: this.uniqueDimensionName,
            resultsLoaded: this.resultsLoaded,
            isLoadingMore: this.isLoadingMore,
            searchTerm: this.searchTerm,
            currentTab: this.currentTab,
            canLoadMoreResults: this.canLoadMoreResults,
            numRecs: this.numRecs,
            searchedTerm: this.searchedTerm,
            adjustedTerm: this.adjustedTerm,
            totalNumRecs: this.totalNumRecs,
            typeaheadResults: this.typeaheadResults,
            isEmptySearch: this.isEmptySearch,
            queryState: this.queryState,
            dynamicTouts: this.dynamicTouts,
            dynamicArticles: this.dynamicArticles,
            currentContentTab: this.getCurrentContentTab,
            contentRecs: this.contentRecs,
            articlesRecsNum: this.articlesRecsNum,
            productRecsNum: this.productRecsNum
        };
    }

    @computed
    private get typeaheadResults() {
        return this.searchRepository.typeahead.dimensionsTypeahead || [];
    }

    @action
    public setCurrentTab = (dimensionName: string | null) => {
        if (this.currentTab === dimensionName) {
            this.currentTab = null;
        } else {
            this.currentTab = dimensionName;
        }
    };

    @action
    public toggleFiltersOverlay = () => {
        this.isFiltersOverlayOpen = !this.isFiltersOverlayOpen;
        const body = document.body;
        const html = document.documentElement;
        if (this.isFiltersOverlayOpen) {
            body.style.overflow = 'hidden';
            html.style.overflow = 'hidden';
        } else {
            body.style.overflowY = 'auto';
            html.style.overflowY = 'auto';
        }
    };

    @action
    public toggleSortOverlay = () => {
        this.isSortOverlayOpen = !this.isSortOverlayOpen;
    };

    @action
    public setContentSearch(value: boolean) {
        this.enableContentSearch = value;
    }

    public get searchUrlParam() {
        const location = <Location>root.location;
        const params = new URLSearchParams(location.search);

        return params.get('search') || '';
    }

    @computed
    public set searchUrlParam(value: string) {
        this.urlUpdateState('search', value);
    }

    public get searchTypeUrlParam() {
        const location = <Location>root.location;
        const params = new URLSearchParams(location.search);

        return params.get('search_type') || '';
    }

    @computed
    public set searchTypeUrlParam(value: string) {
        this.urlUpdateState('search_type', value);
    }

    @computed
    public get navigationStateUrlParam() {
        const location = <Location>root.location;
        const params = new URLSearchParams(location.search);

        return params.get('state') || '';
    }

    public set navigationStateUrlParam(value: string) {
        this.urlUpdateState('state', value);
    }

    @action
    public urlUpdateState = (searchKey: string, searchValue: string) => {
        const location = <Location>root.location;
        const urlParams = new URLSearchParams(location.search);

        if (!!searchValue) {
            urlParams.set(searchKey, searchValue);
        } else {
            urlParams.delete(searchKey);
        }

        window.history.replaceState({}, '', `${window.location.pathname}?${urlParams.toString()}`);
    };

    @action
    public searchParam(value: string, queryState: string) {
        this.searchTerm = value;
        this.queryState = queryState;
        this.searchResults();
    }

    @computed
    private get numRecs() {
        this.searchRepository.searchResults.numRecs.totalNumRecs =
            this.searchRepository.searchResults.numRecs.totalNumRecs - this.dynamicTouts.length;

        return this.searchRepository.searchResults.numRecs;
    }

    @computed
    private get adjustedTerm() {
        return this.searchRepository.searchResults.adjustedTerm;
    }

    private get totalNumRecs() {
        return this.searchRepository.searchResults.productsNumber || 0;
    }

    @computed
    private get productIds() {
        return this.searchRepository.searchResults.productIds || [];
    }

    @computed
    private get skuIds() {
        return this.searchRepository.searchResults.skuIds || [];
    }

    @computed
    private get items() {
        return this.searchRepository.items || [];
    }

    @computed
    private get sortOptions() {
        return this.searchRepository.searchResults.sortByOptions || [];
    }

    @computed
    private get sortOptionSelected() {
        if (this.sortOptions.length > 0) {
            const selectedValue = this.sortOptions.find(
                (sortOption: ISortOption) => sortOption.selected === true
            );

            return selectedValue ? selectedValue.label : this.sortOptionName;
        }

        return this.sortOptionName;
    }

    @computed
    private get breadcrumbs() {
        return this.searchRepository.filters.breadcrumbsFilters || [];
    }

    @computed
    private get navigationFilters() {
        return this.searchRepository.filters.navigationFilters || [];
    }

    @computed
    private get dynamicTouts() {
        return this.searchRepository.searchContentCatalog.touts || [];
    }

    @computed
    private get dynamicArticles() {
        return this.searchRepository.searchArticleCatalog.articles || [];
    }

    @computed
    private get uniqueDimensionName() {
        return this.searchRepository.filters.uniqueDimensionName || [];
    }

    public getFiltersByDimensionName = (dimension: string) => {
        return this.searchRepository.filters.getFiltersByDimensionName(dimension);
    };

    @action
    public sortByQuery = (inputName: string, value: string) => {
        this.sortOptionName = inputName;
        this.queryState = value.replace(/&Ntt=.*/g, '');

        if (this.enableContentSearch) {
            this.refinementSearch = true;
            this.articleSearch = false;
            this.searchResults();
            this.refinementSearch = false;
            this.articleSearch = true;
        } else {
            this.searchResults();
        }
    };

    @action
    public refinementQuery = (ev: string) => {
        this.queryState = ev.replace(/&Ntt=.*|&jsonp=jsonp/g, '');

        if (this.enableContentSearch) {
            this.refinementSearch = true;
            this.articleSearch = false;
            this.searchResults();
            this.refinementSearch = false;
            this.articleSearch = true;
        } else {
            this.searchResults();
        }
    };

    @action
    public searchQuery = (ev: React.FormEvent<HTMLInputElement>) => {
        this.searchTerm = ev.currentTarget.value;
        this.queryState = '';
    };

    @action
    public clearFilters = () => {
        this.queryState = `${'?'}${this.queryState.replace(/(^.*)?\?/g, '')}`;

        if (this.enableContentSearch) {
            this.refinementSearch = true;
            this.articleSearch = false;
            this.searchResults();
            this.refinementSearch = false;
            this.articleSearch = true;
        } else {
            this.searchResults();
        }
    };

    public debounceEndlessScrolling = () => {
        if (this.endlessScrollTimeout) {
            clearTimeout(this.endlessScrollTimeout);
        }
        this.endlessScrollTimeout = setTimeout(
            () => this.handleEndlessScrolling(this.searchRepository.pagination.nextPageLink),
            ENDLESS_SCROLL_DEBOUNCE_LIMIT
        );
    };

    @action
    private handleEndlessScrolling = async (nextPageLink: string) => {
        if (this.shouldLoadMoreResults) {
            this.queryState = `${this.searchRepository.pagination.nextPageLink.replace(
                /&Ntt=.*/g,
                ''
            )}`;
            await this.loadMoreResults();
            this.queryState = '';
        }
    };

    @computed
    public get canLoadMoreResults() {
        return !this.isLoadingMore && this.searchRepository.pagination.hasMoreResults;
    }

    @computed
    private get shouldLoadMoreResults() {
        const element = document.documentElement;
        const scrollTop = window.scrollY;

        if (!this.canLoadMoreResults) {
            return false;
        } else if (
            element.scrollHeight - Math.ceil(scrollTop) - ENDLESS_SCROLL_ALLOWED_PIXELS <=
            element.clientHeight
        ) {
            return true;
        }

        return false;
    }

    public loadMoreResults = async () => {
        try {
            this.isLoadingMore = true;
            if (this.enableContentSearch) {
                this.articleSearch = false;
                this.fetchResults();
                this.articleSearch = true;
            } else {
                this.fetchResults();
            }
        } finally {
            this.isLoadingMore = false;
        }
    };

    @computed
    public get endecaRedirectUrl() {
        return this.searchRepository.searchResults.endecaRedirectUrl;
    }

    @action
    public setSearchDisplayFormat(value: string) {
        this.searchDisplayFormat = value;
    }

    @action
    private checkSearchType() {
        switch (this.searchTypeUrlParam) {
            case VOICE_SEARCH_PARAM:
                this.searchType = VOICE_SEARCH_PARAM;
                break;
            case `${PREDICTIVE_SEARCH_PARAM}${SEARCH_TEXT_PARAM}`:
                this.searchType = `${PREDICTIVE_SEARCH_PARAM}${SEARCH_TEXT_PARAM}`;
                break;
            case TAGS_SEARCH_PARAM:
                this.searchType = TAGS_SEARCH_PARAM;
                break;
            case `${PREDICTIVE_SEARCH_PARAM}${FILTERS_SEARCH_PARAM}`:
                this.searchType = `${PREDICTIVE_SEARCH_PARAM}${FILTERS_SEARCH_PARAM}`;
                break;
            case `${PREDICTIVE_CONTENT_SEARCH_PARAM}`:
                this.searchType = `${PREDICTIVE_CONTENT_SEARCH_PARAM}`;
                break;
            case `${PREDICTIVE_SEARCH_PARAM}${TRENDING_SEARCH_PARAM}`:
                this.searchType = `${PREDICTIVE_SEARCH_PARAM}${TRENDING_SEARCH_PARAM}`;
                break;
            case `${STANDARD_SEARCH_PARAM}_${TRENDING_SEARCH_PARAM}`:
                this.searchType = `${STANDARD_SEARCH_PARAM}_${TRENDING_SEARCH_PARAM}`;
                break;
            default:
                this.searchType = STANDARD_SEARCH_PARAM;
        }

        this.isRedirectSearch = false;
    }

    @action
    public searchResults = async () => {
        try {
            this.currentTab = null;
            this.isLoading = true;
            this.resultsLoaded = false;
            this.setCurrentContentTab(TAB_TYPE.PRODUCTS);
            this.searchRepository.clearRepositoryItems();

            if (this.enableContentSearch && !this.refinementSearch) {
                this.articleSearch = true;
            }

            await this.fetchResults();
            this.trackSearchResults();

            if (this.items.length === 0 && this.dynamicArticles.length) {
                this.setCurrentContentTab(TAB_TYPE.STORIES);
            }
        } finally {
            this.isLoading = false;
            this.resultsLoaded = true;
        }
    };

    @action
    private fetchResults = async () => {
        if (!!this.searchTerm) {
            this.searchedTerm = this.searchTerm;

            if (!!this.isRedirectSearch) {
                this.checkSearchType();
                if (this.searchType === PREDICTIVE_CONTENT_SEARCH_PARAM) {
                    this.setCurrentContentTab(TAB_TYPE.STORIES);
                }
            } else {
                this.contentRecs && this.productRecsNum
                    ? (this.searchType = STANDARD_PRODUCTS_SEARCH_PARAM)
                    : (this.searchType = STANDARD_SEARCH_PARAM);
            }

            const search = {
                searchTerm: this.searchTerm,
                queryState: this.queryState,
                searchDisplayFormat: this.searchDisplayFormat,
                enableContentSearch: this.enableContentSearch,
                articleSearch: this.articleSearch
            };

            this.navigationStateUrlParam = this.queryState;

            const searchLoadedUseCase = new SearchLoadedUseCase(this.searchRepository);

            await searchLoadedUseCase.execute(search);

            if (!!this.endecaRedirectUrl) {
                emit(events.CHANGE_LOCATION, {
                    redirectUrl: `${this.endecaRedirectUrl}${SEARCH_PARAM}${this.searchTerm}${SEARCH_TYPE_PARAM}${STANDARD_SEARCH_PARAM}_${SEARCH_REDIRECT_PARAM}`
                });
            } else {
                this.searchUrlParam = this.searchTerm;
                this.searchTypeUrlParam = this.searchType;
            }

            if (this.items.length === 0) {
                this.queryState = '';
            }
            this.isEmptySearch = false;
        } else {
            this.urlUpdateState('search', '');
            this.urlUpdateState('state', '');
            this.urlUpdateState('search_type', '');
            this.isEmptySearch = true;
        }
    };

    private trackSearchResults() {
        const eventArgs = {
            searchTerm: this.searchTerm,
            searchType: STANDARD_SEARCH_PARAM,
            skuIds: this.skuIds
        };

        emit(events.SEARCH_PRODUCTS_QUERIED, eventArgs);
    }

    public clear() {
        this.queryState = '';
        this.navigationStateUrlParam = this.queryState;
    }

    @action
    public setCurrentContentTab = (id: string) => {
        this.currentContentTab = id;
    };

    @computed
    public get getCurrentContentTab() {
        return this.currentContentTab;
    }

    public onTabSelect = (id: string) => {
        this.setCurrentContentTab(id);
        if (id === TAB_TYPE.STORIES) {
            this.searchType = STANDARD_CONTENT_SEARCH_PARAM;
            this.searchTypeUrlParam = this.searchType;
        }
    };

    @computed
    private get contentRecs() {
        return this.searchRepository.searchContentCatalog.numContentRecs.contentRecNumber;
    }

    @computed
    private get articlesRecsNum() {
        return this.searchRepository.searchArticleCatalog.numContentRecs.articlesRecNumber;
    }

    @computed
    private get productRecsNum() {
        return (
            this.searchRepository.searchResults.numRecs.contentTotalNumRecs -
            this.dynamicTouts.length
        );
    }
}
